/**
 * Organization Work Schedule (V1 scheduling foundation).
 *
 * Stored in organizations.settings.work_schedule (validated + stamped by the
 * database trigger cp_organizations_work_schedule_guard). The organization's
 * timezone stays organizations.timezone; this module never reads the device
 * timezone.
 *
 *   working_weekdays   ISO weekdays 1=Mon .. 7=Sun (any combination, at least one)
 *   minutes_per_day    working time in one working day, 15..1440 (0.25 h .. 24 h).
 *                      A conversion value (duration → working days), not shifts.
 *   non_working_dates  [{ date: "YYYY-MM-DD", label? }] org-level holidays
 *
 * An organization that never configured a schedule uses DEFAULT_WORK_SCHEDULE
 * (Mon–Fri, 8 h). Nothing is backfilled into the database.
 *
 * EXPECTED COMPLETION is DERIVED here, never stored:
 *   - needs start_date AND planned_duration_minutes, otherwise null (never invented);
 *   - working days needed = ceil(duration / minutes_per_day);
 *     a duration of exactly one working day finishes the same day;
 *   - day 1 is the start date when it is a working day, otherwise the next
 *     working day; non-working weekdays and non-working dates are skipped;
 *   - pure calendar-date arithmetic on the org's ISO dates (no time of day, no
 *     timezone conversion), so the device timezone can never shift the result;
 *   - the due date is never read: Due, Duration and Expected Completion are
 *     independent of each other.
 */

export const ISO_WEEKDAYS = Object.freeze([
  { value: 1, short: "Mon", label: "Monday" },
  { value: 2, short: "Tue", label: "Tuesday" },
  { value: 3, short: "Wed", label: "Wednesday" },
  { value: 4, short: "Thu", label: "Thursday" },
  { value: 5, short: "Fri", label: "Friday" },
  { value: 6, short: "Sat", label: "Saturday" },
  { value: 7, short: "Sun", label: "Sunday" },
]);

export const DEFAULT_MINUTES_PER_DAY = 480;
export const MIN_MINUTES_PER_DAY = 15;
export const MAX_MINUTES_PER_DAY = 1440;
export const MAX_NON_WORKING_DATES = 366;
export const MAX_DATE_LABEL = 80;
/** Safety bound for the forward search (about 50 years of calendar days). */
export const MAX_SEARCH_DAYS = 18300;

export const DEFAULT_WORK_SCHEDULE = Object.freeze({
  workingWeekdays: Object.freeze([1, 2, 3, 4, 5]),
  minutesPerDay: DEFAULT_MINUTES_PER_DAY,
  nonWorkingDates: Object.freeze([]),
});

/** Status of the schedule the app is using. */
export const SCHEDULE_STATUS = Object.freeze({
  CONFIGURED: "configured", // the org saved a schedule
  DEFAULT: "default", // the org's settings were loaded and it has none → default
  UNKNOWN: "unknown", // the org's settings are not known (never loaded) → no forecast
});

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DAY_MS = 86400000;

/** "YYYY-MM-DD" → UTC midnight ms, or NaN for an invalid calendar date. */
export function isoToUtcMs(iso) {
  const m = ISO_DATE.exec(String(iso || ""));
  if (!m) return NaN;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const ms = Date.UTC(y, mo - 1, d);
  const back = new Date(ms);
  if (back.getUTCFullYear() !== y || back.getUTCMonth() !== mo - 1 || back.getUTCDate() !== d) return NaN;
  return ms;
}

export function utcMsToIso(ms) {
  return new Date(ms).toISOString().slice(0, 10);
}

/** ISO weekday (1=Mon..7=Sun) of a "YYYY-MM-DD" date; null when invalid. */
export function isoWeekday(iso) {
  const ms = isoToUtcMs(iso);
  if (!Number.isFinite(ms)) return null;
  const d = new Date(ms).getUTCDay();
  return d === 0 ? 7 : d;
}

/**
 * Database document (settings.work_schedule) → { schedule, problem }.
 * A malformed document never becomes a guessed schedule: schedule is null and
 * problem explains why (the database refuses such writes, so this only guards
 * against corrupted caches).
 */
export function parseWorkScheduleDoc(doc) {
  if (!doc || typeof doc !== "object" || Array.isArray(doc)) return { schedule: null, problem: "missing" };
  const days = Array.isArray(doc.working_weekdays) ? doc.working_weekdays : null;
  if (!days || !days.length || days.some((d) => !Number.isInteger(d) || d < 1 || d > 7) || new Set(days).size !== days.length) {
    return { schedule: null, problem: "working_weekdays" };
  }
  const mpd = doc.minutes_per_day;
  if (!Number.isInteger(mpd) || mpd < MIN_MINUTES_PER_DAY || mpd > MAX_MINUTES_PER_DAY) {
    return { schedule: null, problem: "minutes_per_day" };
  }
  const raw = doc.non_working_dates == null ? [] : doc.non_working_dates;
  if (!Array.isArray(raw)) return { schedule: null, problem: "non_working_dates" };
  const dates = [];
  for (const entry of raw) {
    if (!entry || !Number.isFinite(isoToUtcMs(entry.date))) return { schedule: null, problem: "non_working_dates" };
    dates.push({ date: entry.date, label: typeof entry.label === "string" ? entry.label : "" });
  }
  dates.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  return {
    schedule: {
      workingWeekdays: [...days].sort((a, b) => a - b),
      minutesPerDay: mpd,
      nonWorkingDates: dates,
      updatedAt: typeof doc.updated_at === "string" ? doc.updated_at : null,
      updatedBy: typeof doc.updated_by === "string" ? doc.updated_by : null,
    },
    problem: null,
  };
}

/**
 * App settings → the schedule the app must use.
 *   settings.work_schedule === undefined → the org's settings are not known (UNKNOWN)
 *   settings.work_schedule === null      → loaded, never configured (DEFAULT)
 *   settings.work_schedule = {…}         → CONFIGURED (or UNKNOWN when malformed)
 */
export function resolveWorkSchedule(settings) {
  const doc = settings ? settings.work_schedule : undefined;
  if (doc === undefined) return { status: SCHEDULE_STATUS.UNKNOWN, schedule: null };
  if (doc === null) return { status: SCHEDULE_STATUS.DEFAULT, schedule: DEFAULT_WORK_SCHEDULE };
  const { schedule } = parseWorkScheduleDoc(doc);
  if (!schedule) return { status: SCHEDULE_STATUS.UNKNOWN, schedule: null };
  return { status: SCHEDULE_STATUS.CONFIGURED, schedule };
}

export function isWorkingDate(iso, schedule, holidaySet) {
  const wd = isoWeekday(iso);
  if (wd == null || !schedule) return false;
  if (!schedule.workingWeekdays.includes(wd)) return false;
  const set = holidaySet || new Set((schedule.nonWorkingDates || []).map((d) => d.date));
  return !set.has(iso);
}

/** Working days a duration needs: ceil(minutes / minutes_per_day). */
export function workingDaysNeeded(durationMinutes, minutesPerDay) {
  const minutes = Number(durationMinutes);
  const mpd = Number(minutesPerDay);
  if (!Number.isFinite(minutes) || minutes <= 0 || !Number.isFinite(mpd) || mpd <= 0) return null;
  return Math.ceil(minutes / mpd);
}

/**
 * Expected Completion (ISO date) or null.
 *   computeExpectedCompletion({ startDate: "2026-11-02", durationMinutes: 720 }, schedule) → "2026-11-03"
 * Never reads a due date.
 */
export function computeExpectedCompletion({ startDate, durationMinutes } = {}, schedule) {
  if (!schedule || !startDate || durationMinutes == null) return null;
  const startMs = isoToUtcMs(startDate);
  const needed = workingDaysNeeded(durationMinutes, schedule.minutesPerDay);
  if (!Number.isFinite(startMs) || !needed) return null;
  if (!Array.isArray(schedule.workingWeekdays) || !schedule.workingWeekdays.length) return null;
  const holidays = new Set((schedule.nonWorkingDates || []).map((d) => d.date));
  let counted = 0;
  for (let i = 0; i <= MAX_SEARCH_DAYS; i += 1) {
    const iso = utcMsToIso(startMs + i * DAY_MS);
    if (isWorkingDate(iso, schedule, holidays)) {
      counted += 1;
      if (counted >= needed) return iso;
    }
  }
  return null;
}

/** Same as computeExpectedCompletion for a project task row. */
export function expectedCompletionForTask(task, schedule) {
  if (!task) return null;
  return computeExpectedCompletion({ startDate: task.start_date, durationMinutes: task.planned_duration_minutes }, schedule);
}

const WEEKDAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2026-11-03" → "Tue, Nov 3" (year added when it differs from yearOf). */
export function formatScheduleDate(iso, { withYear = false } = {}) {
  const ms = isoToUtcMs(iso);
  if (!Number.isFinite(ms)) return "";
  const d = new Date(ms);
  const base = `${WEEKDAY_SHORT[d.getUTCDay()]}, ${MONTH_SHORT[d.getUTCMonth()]} ${d.getUTCDate()}`;
  return withYear ? `${base}, ${d.getUTCFullYear()}` : base;
}

/** "Mon–Fri", "Mon–Sat", "Mon, Wed, Fri", "Every day". */
export function describeWeekdays(days = []) {
  const sorted = [...new Set(days)].filter((d) => d >= 1 && d <= 7).sort((a, b) => a - b);
  if (sorted.length === 7) return "Every day";
  if (!sorted.length) return "No working days";
  const contiguous = sorted.every((d, i) => i === 0 || d === sorted[i - 1] + 1);
  const name = (d) => ISO_WEEKDAYS[d - 1].short;
  if (contiguous && sorted.length >= 3) return `${name(sorted[0])}–${name(sorted[sorted.length - 1])}`;
  return sorted.map(name).join(", ");
}

/** 480 → "8 h", 630 → "10.5 h", 1440 → "24 h". */
export function formatHoursPerDay(minutes) {
  const hours = Math.round((Number(minutes) / 60) * 100) / 100;
  return `${hours} h`;
}

// ── admin form ────────────────────────────────────────────────────────────────

export function scheduleToForm(schedule) {
  const s = schedule || DEFAULT_WORK_SCHEDULE;
  return {
    workingWeekdays: [...s.workingWeekdays],
    hoursPerDay: String(Math.round((s.minutesPerDay / 60) * 100) / 100),
    nonWorkingDates: (s.nonWorkingDates || []).map((d) => ({ date: d.date, label: d.label || "" })),
  };
}

/**
 * Admin form → { ok, doc, errors } where doc is the settings.work_schedule
 * document to save (the server adds version/updated_at/updated_by).
 */
export function formToScheduleDoc(form = {}) {
  const errors = {};
  const days = [...new Set((form.workingWeekdays || []).map(Number))].filter((d) => Number.isInteger(d) && d >= 1 && d <= 7).sort((a, b) => a - b);
  if (!days.length) errors.workingWeekdays = "Choose at least one working day.";
  const text = String(form.hoursPerDay ?? "").trim().replace(",", ".");
  const hours = /^\d+(\.\d+)?$/.test(text) ? Number(text) : NaN;
  const minutes = Math.round(hours * 60);
  if (!Number.isFinite(hours) || hours <= 0) errors.hoursPerDay = "Enter the hours in a working day (more than 0).";
  else if (hours > 24) errors.hoursPerDay = "A working day cannot be longer than 24 hours.";
  else if (minutes < MIN_MINUTES_PER_DAY) errors.hoursPerDay = "A working day must be at least 0.25 hours.";
  const dates = [];
  const seen = new Set();
  for (const entry of form.nonWorkingDates || []) {
    const date = String(entry?.date || "").trim();
    const label = String(entry?.label || "").trim();
    if (!date && !label) continue;
    if (!Number.isFinite(isoToUtcMs(date))) {
      errors.nonWorkingDates = "Each non-working date needs a valid date.";
      break;
    }
    if (seen.has(date)) {
      errors.nonWorkingDates = `${date} is listed twice.`;
      break;
    }
    if (label.length > MAX_DATE_LABEL) {
      errors.nonWorkingDates = `Keep labels to ${MAX_DATE_LABEL} characters.`;
      break;
    }
    seen.add(date);
    dates.push(label ? { date, label } : { date });
  }
  if (dates.length > MAX_NON_WORKING_DATES) errors.nonWorkingDates = `At most ${MAX_NON_WORKING_DATES} non-working dates.`;
  dates.sort((a, b) => (a.date < b.date ? -1 : 1));
  if (Object.keys(errors).length) return { ok: false, doc: null, errors };
  return { ok: true, doc: { version: 1, working_weekdays: days, minutes_per_day: minutes, non_working_dates: dates }, errors };
}
